'use client';
import {
    PublicBlendedCourse,
    PublicClassCourse,
    PublicCourse,
    PublicEducator,
    PublicOccurrence,
} from '@cademy-package/sdk-component-types';
import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import styles from './styles.module.scss';
import SeeMoreButton from './SeeMoreButton';
import { EmptyListPlaceholder } from './EmptyListPlaceholder';
import { OccurrenceListItem } from './OccurrenceListItem';
import LocationSelect, { LocationSelectOption } from '../LocationSelect';
import { getFilteredOccurrences } from '../../services/getFilteredOccurrences';
import { useQueryParam } from '../../hooks/useQueryParam';
import { useSearchParams } from 'next/navigation';
import { isChildOccurrence } from '../../services/occurrences/isChildOccurrence';

type OccurrenceListProps = {
    course: PublicCourse;
    educator: PublicEducator;
    hidePricing?: boolean;
    occurrences: Array<PublicOccurrence>;
    occurrencesPerPage: number;
    onOccurrenceSelected: (occurrence: PublicOccurrence) => void;
};

const courseHasDates = (
    course: PublicCourse
): course is PublicClassCourse | PublicBlendedCourse => {
    if (course.type === 'on-request') {
        return false;
    }
    if (course.type === 'on-demand') {
        return false;
    }
    return true;
};

export const OccurrenceList: FunctionComponent<OccurrenceListProps> = ({
    course,
    educator,
    hidePricing,
    occurrences,
    occurrencesPerPage,
    onOccurrenceSelected,
}) => {
    const [showAllOccurrences, setShowAllOccurrences] = useState(false);
    const toggleShowAllOccurrences = useCallback(
        () => setShowAllOccurrences(!showAllOccurrences),
        [showAllOccurrences]
    );
    const topLevelOccurrences: Array<PublicOccurrence> = useMemo(() => {
        return occurrences.filter((occurrence) => {
            if (!isChildOccurrence(occurrence)) {
                return true;
            }

            if (occurrence.individually_bookable) {
                const hasParentBlock = occurrences.some(
                    (occ) => !isChildOccurrence(occ) && occ._id === occurrence.parent_ref
                );
                return !hasParentBlock;
            }

            return false;
        });
    }, [occurrences]);
    const searchParams = useSearchParams();
    const [locationFilter, setLocationFilter] = useState<LocationSelectOption | null>(null);
    const [locationParam, setLocationParam] = useQueryParam(
        'location',
        searchParams?.get('location') || null
    );
    const [currentOccurrences, setCurrentOccurrences] =
        useState<Array<PublicOccurrence>>(topLevelOccurrences);

    const handleSetLocationFilter = useCallback(
        (selectedLocationOption: LocationSelectOption) => {
            setLocationParam(selectedLocationOption.value);
            setLocationFilter(selectedLocationOption);
        },
        [setLocationParam]
    );

    const visibleOccurrences = useMemo(() => {
        if (showAllOccurrences) {
            return currentOccurrences;
        }
        return currentOccurrences.slice(0, occurrencesPerPage);
    }, [occurrencesPerPage, currentOccurrences, showAllOccurrences]);

    useEffect(() => {
        if (course.type === 'on-request' || course.type === 'on-demand') {
            return;
        }
        setCurrentOccurrences(getFilteredOccurrences(locationFilter, topLevelOccurrences, course));
    }, [locationFilter, topLevelOccurrences, course]);

    if (!courseHasDates(course)) {
        return <EmptyListPlaceholder course={course} />;
    }

    return (
        <section className={styles.occurrencesSection}>
            <div className={styles.locationFilters}>
                <LocationSelect
                    course={course}
                    value={locationParam}
                    occurrences={topLevelOccurrences}
                    onChange={handleSetLocationFilter}
                />
            </div>
            <ul className={styles.occurrenceList}>
                {visibleOccurrences.map((occurrence) => {
                    return (
                        <OccurrenceListItem
                            key={occurrence._id}
                            occurrence={occurrence}
                            course={course}
                            educator={educator}
                            onOccurrenceSelected={onOccurrenceSelected}
                            occurrences={occurrences}
                            hidePricing={hidePricing}
                            vatSettings={educator.vat_settings}
                        />
                    );
                })}
            </ul>
            <SeeMoreButton
                preset="primary"
                shownOccurrences={currentOccurrences}
                showAllOccurrences={showAllOccurrences}
                showOccurrencesClickHandler={toggleShowAllOccurrences}
                occurrencesPerPage={occurrencesPerPage}
            />
        </section>
    );
};
